import React, { useState, useEffect } from 'react';

const Navigation = ({ currentPage, setCurrentPage }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    let ticking = false;
    let wasScrolled = window.scrollY > 60;
    setScrolled(wasScrolled);

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const nextScrolled = window.scrollY > 60;
          if (nextScrolled !== wasScrolled) {
            wasScrolled = nextScrolled;
            setScrolled(nextScrolled);
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [menuOpen]);

  const navItems = ['Home', 'About', 'Services', 'Process', 'Outcomes', 'Contact'];
  const portalUrl = 'https://portal.symphony-strategies.com';

  return (
    <nav className={`nav-shell ${scrolled ? 'nav-shell--scrolled' : ''} ${menuOpen ? 'nav-shell--open' : ''}`} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: menuOpen ? 0 : 'auto',
      zIndex: 1000,
      padding: scrolled ? '12px 40px' : '28px 48px',
      background: menuOpen ? 'var(--ivory)' : 'transparent',
      borderBottom: '1px solid transparent',
      transition: 'padding var(--dur-fast) var(--ease-premium), background-color var(--dur-fast) var(--ease-premium)',
      transform: 'translateZ(0)',
      willChange: 'transform',
    }}>
      <div className="nav-inner" style={{
        maxWidth: '1400px',
        margin: '0 auto',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: scrolled ? '10px 16px 10px 18px' : '0',
        borderRadius: scrolled ? 'var(--radius-nav)' : '0',
        background: scrolled ? 'rgba(250, 247, 242, 0.9)' : 'transparent',
        backdropFilter: scrolled ? 'blur(22px) saturate(160%)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(22px) saturate(160%)' : 'none',
        border: scrolled ? '1px solid rgba(197, 160, 89, 0.16)' : '1px solid transparent',
        boxShadow: scrolled ? '0 18px 45px rgba(10, 14, 23, 0.08), 0 2px 10px rgba(10, 14, 23, 0.035), inset 0 1px 0 rgba(255, 255, 255, 0.55)' : 'none',
        transition: 'padding var(--dur-fast) var(--ease-premium), border-radius var(--dur-fast) var(--ease-premium), background-color var(--dur-fast) var(--ease-premium), border-color var(--dur-fast) var(--ease-premium), box-shadow var(--dur-fast) var(--ease-premium)',
      }}>
        <div
          onClick={() => setCurrentPage('Home')}
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
          }}
        >
          <img
            src="/logo-icon.png"
            alt="Symphony Strategies"
            className="nav-logo"
            loading="eager"
            decoding="async"
            onError={(e) => {
              if (e.currentTarget.src.endsWith('/logo.png')) return;
              e.currentTarget.src = '/logo.png';
            }}
            style={{
              height: scrolled ? '34px' : '44px',
              transition: 'height var(--dur-fast) var(--ease-premium), transform var(--dur-fast) var(--ease-premium)',
            }}
          />
          <span
            className="nav-company-name"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: scrolled ? '19px' : '24px',
              fontWeight: 500,
              color: 'var(--ink)',
              letterSpacing: '-0.02em',
              transition: 'font-size var(--dur-fast) var(--ease-premium), color var(--dur-fast) var(--ease-premium), transform var(--dur-fast) var(--ease-premium)',
            }}
          >
            <span className="nav-company-full">Symphony Strategies</span>
            <span className="nav-company-short">Symphony</span>
          </span>
        </div>

        <div style={{ display: 'flex', gap: scrolled ? '8px' : '28px', alignItems: 'center', transition: 'gap var(--dur-fast) var(--ease-premium)' }} className="desktop-nav">
          {navItems.map(item => (
            <button
              key={item}
              type="button"
              onClick={() => setCurrentPage(item)}
              className={`nav-item ${currentPage === item ? 'nav-item--active' : ''}`}
              style={{
                background: 'none',
                border: 'none',
                fontFamily: "'Outfit', sans-serif",
                fontSize: '11px',
                fontWeight: currentPage === item ? 500 : 400,
                cursor: 'pointer',
                position: 'relative',
                padding: scrolled ? '10px 13px' : '10px 0',
                transition: 'color var(--dur-fast) var(--ease-premium), background-color var(--dur-fast) var(--ease-premium), padding var(--dur-fast) var(--ease-premium), border-radius var(--dur-fast) var(--ease-premium)',
                borderRadius: scrolled ? 'var(--radius-pill)' : '0',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
              }}
            >
              {item}
              <span className="nav-underline" style={{
                position: 'absolute',
                bottom: scrolled ? '5px' : '2px',
                left: '50%',
                transform: 'translateX(-50%)',
                height: '1px',
                transition: 'width var(--dur-fast) var(--ease-premium), opacity var(--dur-fast) var(--ease-premium), background-color var(--dur-fast) var(--ease-premium)',
              }} />
            </button>
          ))}

          <a
            href={portalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="nav-portal"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: "'Outfit', sans-serif",
              fontSize: '11px',
              fontWeight: 500,
              cursor: 'pointer',
              padding: '10px 18px',
              borderRadius: 'var(--radius-pill)',
              border: '1px solid',
              transition: 'color var(--dur-fast) var(--ease-premium), background-color var(--dur-fast) var(--ease-premium), border-color var(--dur-fast) var(--ease-premium), transform var(--dur-fast) var(--ease-premium), box-shadow var(--dur-fast) var(--ease-premium)',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              textDecoration: 'none',
            }}
          >
            Client Portal
            <span aria-hidden="true" style={{
              fontSize: '11px',
              color: 'var(--brass)',
              transform: 'translateY(-0.5px)',
              transition: 'transform var(--dur-base) var(--ease-premium)',
            }}>🔒</span>
          </a>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen(!menuOpen)}
          className="mobile-menu-btn"
          aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={menuOpen}
          style={{
            display: 'none',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '12px',
            width: '48px',
            height: '48px',
            position: 'relative',
          }}
        >
          <span style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '22px',
            height: '1.5px',
            background: 'var(--ink)',
            transform: menuOpen
              ? 'translate(-50%, -50%) rotate(45deg)'
              : 'translate(-50%, calc(-50% - 5px))',
            transition: 'transform var(--dur-fast) var(--ease-standard)',
          }} />
          <span style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '22px',
            height: '1.5px',
            background: 'var(--ink)',
            opacity: menuOpen ? 0 : 1,
            transform: 'translate(-50%, -50%)',
            transition: 'opacity var(--dur-fast) var(--ease-standard)',
          }} />
          <span style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '22px',
            height: '1.5px',
            background: 'var(--ink)',
            transform: menuOpen
              ? 'translate(-50%, -50%) rotate(-45deg)'
              : 'translate(-50%, calc(-50% + 5px))',
            transition: 'transform var(--dur-fast) var(--ease-standard)',
          }} />
        </button>
      </div>

      {menuOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'var(--ivory)',
          padding: '100px 40px 40px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          zIndex: 999,
          overflowY: 'auto',
        }}>
          {navItems.map((item, i) => (
            <button
              key={item}
              type="button"
              onClick={() => { setCurrentPage(item); setMenuOpen(false); }}
              style={{
                background: 'none',
                border: 'none',
                fontFamily: "'Playfair Display', serif",
                fontSize: '32px',
                fontWeight: 400,
                color: currentPage === item ? 'var(--ink)' : 'var(--stone)',
                cursor: 'pointer',
                textAlign: 'left',
                padding: '16px 0',
                borderBottom: '1px solid var(--pearl)',
                opacity: 0,
                animation: `slideUp var(--dur-slow) var(--ease-premium) ${i * 80}ms forwards`,
              }}
            >
              {item}
            </button>
          ))}

          <a
            href={portalUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              marginTop: '24px',
              padding: '18px 18px',
              borderRadius: '14px',
              border: '1px solid rgba(184, 149, 110, 0.35)',
              background: 'rgba(184, 149, 110, 0.06)',
              fontFamily: "'Playfair Display', serif",
              fontSize: '26px',
              fontWeight: 400,
              color: 'var(--navy)',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
              opacity: 0,
              animation: `slideUp var(--dur-slow) var(--ease-premium) ${navItems.length * 80}ms forwards`,
            }}
            onClick={() => setMenuOpen(false)}
          >
            <span>Client Portal</span>
            <span aria-hidden="true" style={{
              fontSize: '18px',
              color: 'var(--brass)',
            }}>🔒</span>
          </a>
        </div>
      )}

      <style>{`
        .nav-company-short { display: none; }
        @media (max-width: 900px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: block !important; }
          .nav-shell { padding-left: 24px !important; padding-right: 24px !important; }
          .nav-inner { padding: 8px 10px !important; border-radius: var(--radius-nav) !important; background: rgba(250, 247, 242, 0.92) !important; border: 1px solid rgba(197, 160, 89, 0.13) !important; box-shadow: 0 12px 32px rgba(10,14,23,0.07) !important; backdrop-filter: blur(18px) saturate(150%) !important; -webkit-backdrop-filter: blur(18px) saturate(150%) !important; }
          .nav-company-full { display: none !important; }
          .nav-company-short { display: inline !important; }
          .nav-company-name { font-size: 20px !important; }
        }
        @media (max-width: 768px) {
          .nav-logo { height: 32px !important; }
        }
        @media (max-width: 480px) {
          .nav-company-name { display: inline !important; font-size: 20px !important; }
        }
      `}</style>
    </nav>
  );
};


// Home Page - Premium editorial design with cinematic intro

const Footer = ({ setCurrentPage }) => (
  <footer style={{
    padding: '120px 48px 60px',
    background: 'var(--ink)',
    position: 'relative',
  }}>
    {/* Subtle gradient top accent */}
    <div style={{
      position: 'absolute',
      top: 0,
      left: '0',
      right: '0',
      height: '1px',
      background: 'linear-gradient(to right, transparent, rgba(197, 160, 89, 0.4), transparent)',
    }} />

    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      {/* Main footer content */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: '48px',
        marginBottom: '80px',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            <img
              src="/logo-transparent.png"
              alt="Symphony Strategies"
              loading="lazy"
              decoding="async"
              onError={(e) => {
                if (e.currentTarget.src.endsWith('/logo.png')) return;
                e.currentTarget.src = '/logo.png';
              }}
              style={{
                height: '52px',
                filter: 'brightness(1.4)',
              }}
            />
            <span style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: '22px',
              fontWeight: 500,
              color: 'var(--white)',
              letterSpacing: '-0.01em',
            }}>
              Symphony Strategies
            </span>
          </div>
          <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.5)', maxWidth: '340px', lineHeight: 1.7 }}>
            Family office design and operation for entrepreneurial families.
          </p>
        </div>

        {/* Contact CTA */}
        <div style={{ textAlign: 'right' }}>
          <p style={{
            fontSize: '10px',
            color: 'var(--brass)',
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            marginBottom: '20px',
            fontWeight: 500,
          }}>
            Get in Touch
          </p>
          <button
            type="button"
            onClick={() => {
              if (setCurrentPage) {
                setCurrentPage('Contact');
                window.scrollTo({ top: 0, behavior: 'auto' });
              }
            }}
            style={{
              background: 'transparent',
              border: '1px solid var(--brass)',
              borderRadius: 'var(--radius-pill)',
              padding: '14px 36px',
              fontSize: '11px',
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 500,
              color: 'rgba(255,255,255,0.8)',
              cursor: 'pointer',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              transition: 'height var(--dur-base) var(--ease-premium), font-size var(--dur-base) var(--ease-premium), color var(--dur-base) var(--ease-premium), opacity var(--dur-base) var(--ease-premium), transform var(--dur-base) var(--ease-premium), background-color var(--dur-base) var(--ease-premium), box-shadow var(--dur-base) var(--ease-premium)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--brass)';
              e.currentTarget.style.color = 'var(--ink)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'rgba(255,255,255,0.8)';
            }}
          >
            Start a Conversation
          </button>
        </div>
      </div>

      {/* Bottom bar */}
      <div style={{
        paddingTop: '32px',
        borderTop: '1px solid rgba(255,255,255,0.03)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.25)', letterSpacing: '0.04em' }}>
          © {new Date().getFullYear()} Symphony Strategies, LLC. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

// Intro Animation Overlay - Cinematic reveal on first load

const IntroOverlay = ({ onComplete }) => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      onComplete();
      return;
    }

    // Phase 1: Show overlay with logo
    const timer1 = setTimeout(() => setPhase(1), 100);
    // Phase 2: Line draws
    const timer2 = setTimeout(() => setPhase(2), 400);
    // Phase 3: Fade out overlay
    const timer3 = setTimeout(() => setPhase(3), 1200);
    // Phase 4: Complete - remove overlay
    const timer4 = setTimeout(() => onComplete(), 1800);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, [onComplete]);

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 10000,
      background: 'var(--navy)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      opacity: phase >= 3 ? 0 : 1,
      pointerEvents: phase >= 3 ? 'none' : 'auto',
      transition: 'opacity var(--dur-slow) var(--ease-premium)',
    }}>
      {/* Brass accent glow */}
      <div style={{
        position: 'absolute',
        width: '400px',
        height: '400px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(184, 149, 110, 0.15) 0%, transparent 70%)',
        filter: 'blur(60px)',
        opacity: phase >= 1 ? 1 : 0,
        transition: 'opacity var(--dur-slow) var(--ease-standard)',
      }} />

      {/* Logo mark */}
      <div style={{
        opacity: phase >= 1 ? 1 : 0,
        transform: phase >= 1 ? 'scale(1)' : 'scale(0.9)',
        transition: 'height var(--dur-base) var(--ease-premium), font-size var(--dur-base) var(--ease-premium), color var(--dur-base) var(--ease-premium), opacity var(--dur-base) var(--ease-premium), transform var(--dur-base) var(--ease-premium), background-color var(--dur-base) var(--ease-premium), box-shadow var(--dur-base) var(--ease-premium)',
        marginBottom: '32px',
      }}>
        <img
          src="/logo-transparent.png"
          alt=""
          loading="eager"
          decoding="async"
          onError={(e) => {
            if (e.currentTarget.src.endsWith('/logo.png')) return;
            e.currentTarget.src = '/logo.png';
          }}
          style={{
            height: '80px',
            filter: 'brightness(1.4)',
          }}
        />
      </div>

      {/* Animated line */}
      <div style={{
        width: phase >= 2 ? '140px' : '0px',
        height: '1px',
        background: 'var(--brass)',
        transition: 'width var(--dur-ultra) var(--ease-premium)',
        marginBottom: '28px',
      }} />

      {/* Company name */}
      <div style={{
        fontFamily: "'Playfair Display', serif",
        fontSize: '22px',
        fontWeight: 400,
        color: 'var(--white)',
        letterSpacing: '0.15em',
        opacity: phase >= 2 ? 1 : 0,
        transform: phase >= 2 ? 'translateY(0)' : 'translateY(12px)',
        transition: 'opacity var(--dur-slow) var(--ease-premium) 0.2s, transform var(--dur-slow) var(--ease-premium) 0.2s',
      }}>
        Symphony Strategies
      </div>
    </div>
  );
};

export { Navigation, Footer, IntroOverlay };
